import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Card, Alert, Input, Row, Col } from 'antd';
import { CameraOutlined, StopOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/library';
import './BarcodeScanner.css';

const BarcodeScannerModal = ({ 
  visible, 
  onClose, 
  onScan, 
  title = "Scan Barcode",
  allowManualInput = true 
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [manualInput, setManualInput] = useState('');
  
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const manualInputRef = useRef(null);
  const manualSequenceStartRef = useRef(0);
  const manualLastChangeRef = useRef(0);
  const manualLastLengthRef = useRef(0);

  useEffect(() => {
    // Initialize the code reader
    codeReader.current = new BrowserMultiFormatReader();
    
    return () => {
      // Cleanup on component unmount
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  // Focus manual input when modal becomes visible so HID scanners can type directly into it
  useEffect(() => {
    if (visible && allowManualInput && manualInputRef.current) {
      const id = setTimeout(() => {
        manualInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(id);
    }
  }, [visible, allowManualInput]);

  const startScanning = async () => {
    try {
      setIsLoading(true);
      setError('');
      setStatus('Initializing camera...');
      
      // Get available video input devices
      const videoInputDevices = await codeReader.current.listVideoInputDevices();
      
      if (videoInputDevices.length === 0) {
        throw new Error('No camera devices found');
      }

      // Prefer back camera for better barcode scanning
      let selectedDeviceId = videoInputDevices[0].deviceId;
      
      // Look for back camera (usually has "back" in label or is not "front")
      const backCamera = videoInputDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        (!device.label.toLowerCase().includes('front') && !device.label.toLowerCase().includes('selfie'))
      );
      
      if (backCamera) {
        selectedDeviceId = backCamera.deviceId;
      }

      setStatus('Starting camera...');
      setIsScanning(true);

      // Start decoding from video device
      await codeReader.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, error) => {
          if (result) {
            const code = result.getText();
            setScannedCode(code);
            setStatus('Barcode scanned successfully!');
            stopScanning();
          }
          if (error && !(error instanceof Error)) {
            // This is a normal decoding error, not a real error
            console.log('Decoding attempt failed:', error);
          }
        }
      );
      
      setStatus('Camera ready - Point camera at barcode');
      setIsLoading(false);
      
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError(`Failed to start camera: ${err.message}`);
      setStatus('');
      setIsScanning(false);
      setIsLoading(false);
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    setIsScanning(false);
    setStatus('Scanner stopped');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(scannedCode);
      setStatus('Code copied to clipboard!');
      setTimeout(() => setStatus(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const clearResult = () => {
    setScannedCode('');
    setManualInput('');
    setStatus('');
    setError('');
  };

  const handleUseScannedCode = () => {
    if (scannedCode) {
      onScan(scannedCode);
      handleClose();
    }
  };

  const handleUseManualCode = () => {
    const value = manualInput.trim();
    if (!value) return;
    onScan(value);
    handleClose();
  };

  // Detect fast text entry patterns typical of hardware barcode scanners
  const handleManualChange = (e) => {
    const value = e.target.value;
    const now = Date.now();
    const prevLen = manualLastLengthRef.current || 0;
    const lastChange = manualLastChangeRef.current || 0;

    if (!lastChange || now - lastChange > 300) {
      manualSequenceStartRef.current = now;
    }

    manualLastChangeRef.current = now;
    manualLastLengthRef.current = value.length;
    setManualInput(value);

    const seqStart = manualSequenceStartRef.current || now;
    const isScannerLike = value.length >= 6 && (now - seqStart) <= 180 && value.length - prevLen >= 4;

    if (isScannerLike) {
      const trimmed = value.trim();
      if (trimmed) {
        onScan(trimmed);
        setManualInput('');
        manualLastLengthRef.current = 0;
        manualLastChangeRef.current = 0;
        manualSequenceStartRef.current = 0;
        handleClose();
      }
    }
  };

  const handleClose = () => {
    stopScanning();
    clearResult();
    onClose();
  };

  const getStatusAlert = () => {
    if (error) {
      return <Alert message={error} type="error" showIcon />;
    }
    if (status) {
      const type = status.includes('success') ? 'success' : 
                   status.includes('error') ? 'error' : 'info';
      return <Alert message={status} type={type} showIcon />;
    }
    return null;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CameraOutlined style={{ color: '#1890ff' }} />
          <span>{title}</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width="90%"
      style={{ maxWidth: '600px' }}
      footer={null}
      destroyOnClose
      centered
    >
      <div className="scanner-container" style={{ padding: '0' }}>
        {getStatusAlert()}

        {/* Camera Section */}
        <Card 
          style={{ 
            marginBottom: '16px',
            borderRadius: '12px',
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: '0' }}
        >
          <div className="video-container" style={{ 
            position: 'relative',
            background: '#000',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <video
              ref={videoRef}
              style={{ 
                width: '100%', 
                height: '300px',
                objectFit: 'cover',
                display: isScanning ? 'block' : 'none'
              }}
              playsInline
              muted
            />
            
            {!isScanning && (
              <div style={{
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                color: '#666'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <CameraOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>
                    Camera Ready
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    Click "Start Scanning" to begin
                  </div>
                </div>
              </div>
            )}

            {isScanning && (
              <div className="scanning-overlay">
                <div className="scanning-line" />
                
                {/* Corner guides */}
                <div className="corner-guide top-left" />
                <div className="corner-guide top-right" />
                <div className="corner-guide bottom-left" />
                <div className="corner-guide bottom-right" />
              </div>
            )}
          </div>
        </Card>

        {/* Controls */}
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            {!isScanning ? (
              <Button
                type="primary"
                size="large"
                icon={<CameraOutlined />}
                onClick={startScanning}
                loading={isLoading}
                disabled={isLoading}
                block
                className="scanner-button"
                style={{ 
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                {isLoading ? 'Starting Camera...' : 'Start Scanning'}
              </Button>
            ) : (
              <Button
                type="default"
                size="large"
                icon={<StopOutlined />}
                onClick={stopScanning}
                block
                className="scanner-button"
                style={{ 
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                Stop Scanning
              </Button>
            )}
          </Col>
          
          {scannedCode && (
            <Col xs={24} sm={12}>
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={startScanning}
                block
                style={{ 
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                Scan Again
              </Button>
            </Col>
          )}
        </Row>

        {/* Results */}
        {scannedCode && (
          <Card 
            className="result-container"
            style={{ 
              marginBottom: '16px',
              borderRadius: '12px',
              border: '2px solid #52c41a',
              background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="result-title">
                ✓ Barcode Scanned Successfully
              </div>
              <div className="result-code">
                {scannedCode}
              </div>
              
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Button 
                    type="primary" 
                    onClick={handleUseScannedCode}
                    block
                    size="large"
                    style={{ 
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  >
                    Use This Barcode
                  </Button>
                </Col>
                <Col xs={24} sm={12}>
                  <Button 
                    onClick={copyToClipboard}
                    icon={<CopyOutlined />}
                    block
                    size="large"
                    style={{ 
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '16px'
                    }}
                  >
                    Copy
                  </Button>
                </Col>
              </Row>
            </div>
          </Card>
        )}

        {/* Manual Input */}
        {allowManualInput && (
          <Card 
            className="manual-input-container"
            style={{ 
              borderRadius: '12px',
              background: '#fafafa'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                Or Enter Manually
              </div>
              <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                Type the barcode if scanning doesn't work
              </div>
            </div>
            
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={16}>
                <Input
                  placeholder="Type barcode manually"
                  value={manualInput}
                  onChange={handleManualChange}
                  onPressEnter={handleUseManualCode}
                  size="large"
                  ref={manualInputRef}
                  style={{ 
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Button 
                  type="primary"
                  onClick={handleUseManualCode}
                  disabled={!manualInput.trim()}
                  size="large"
                  block
                  style={{ 
                    borderRadius: '8px',
                    height: '40px',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  Use
                </Button>
              </Col>
            </Row>
          </Card>
        )}
      </div>

      <style jsx>{`
        @keyframes scanning {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </Modal>
  );
};

export default BarcodeScannerModal;
